// Kuwait UI — behaviour for the shared components.
//
// Search: a real input backed by Frappe's global search, which only returns
// records the signed-in user may read. Remind: a pressed state on the pill
// and a single hook where a reminder backend attaches later. Language: the
// English/Arabic switch on the More page, through the existing preference API.
// Navigation: a tap on a link fetches the next page and swaps the app in
// place, so the product feels like one screen changing rather than a site
// reloading (see the navigation section for the rules).

(function () {
	"use strict";

	function ready(fn) {
		if (document.readyState !== "loading") fn();
		else document.addEventListener("DOMContentLoaded", fn);
	}

	function translate(text) {
		return typeof window.__ === "function" ? window.__(text) : text;
	}

	function escape(text) {
		return String(text == null ? "" : text).replace(/[&<>"']/g, function (c) {
			return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
		});
	}

	function csrf() {
		return (window.frappe && window.frappe.csrf_token) || "";
	}

	// ------------------------------------------------------------------ search

	function slug(doctype) {
		return String(doctype).toLowerCase().replace(/ /g, "-");
	}

	function setupSearch(form) {
		var input = form.querySelector("input");
		var results = form.querySelector(".kui-search-results");
		if (!input || !results) return;

		var timer = null;
		var last = "";
		var links = [];

		function close() {
			results.hidden = true;
			results.innerHTML = "";
			links = [];
		}

		function note(text) {
			results.innerHTML = '<div class="kui-search-note">' + escape(text) + "</div>";
			results.hidden = false;
		}

		function render(rows) {
			if (!rows || !rows.length) {
				note(translate("No matches yet"));
				return;
			}
			results.innerHTML = rows
				.map(function (row) {
					var snippet = String(row.content || "").split("|||")[0].trim();
					return (
						'<a href="/app/' +
						encodeURIComponent(slug(row.doctype)) +
						"/" +
						encodeURIComponent(row.name) +
						'"><strong>' +
						escape(row.name) +
						"</strong><small>" +
						escape(translate(row.doctype)) +
						(snippet ? " · " + escape(snippet.slice(0, 80)) : "") +
						"</small></a>"
					);
				})
				.join("");
			results.hidden = false;
			links = Array.prototype.slice.call(results.querySelectorAll("a"));
		}

		function query(text) {
			var url =
				"/api/method/frappe.utils.global_search.search?text=" +
				encodeURIComponent(text) +
				"&limit=8";
			fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
				.then(function (response) {
					if (!response.ok) throw new Error(String(response.status));
					return response.json();
				})
				.then(function (payload) {
					if (input.value.trim() !== text) return; // stale
					render(payload.message || []);
				})
				.catch(function () {
					note(translate("Search isn't available right now"));
				});
		}

		input.addEventListener("input", function () {
			var text = input.value.trim();
			clearTimeout(timer);
			if (text.length < 2) {
				close();
				return;
			}
			timer = setTimeout(function () {
				if (text === last && !results.hidden) return;
				last = text;
				query(text);
			}, 250);
		});

		form.addEventListener("submit", function (event) {
			// The form's native action (the desk) is a fallback for no-JS only;
			// with JS the first result is the answer to Enter.
			event.preventDefault();
			if (links.length) links[0].click();
		});

		input.addEventListener("keydown", function (event) {
			if (event.key === "Escape") close();
		});

		form.kuiCloseSearch = close;
	}

	// One listener for every search form, however many pages are swapped in.
	document.addEventListener("click", function (event) {
		document.querySelectorAll("[data-kui-search]").forEach(function (form) {
			if (form.kuiCloseSearch && !form.contains(event.target)) form.kuiCloseSearch();
		});
	});

	// ------------------------------------------------------------------ remind

	function setupRemind(control) {
		control.addEventListener("click", function () {
			// Pressed state, then the link proceeds to where a reminder can be
			// sent today. A reminder service plugs in here without touching
			// the markup: preventDefault, call it, show the confirmation.
			control.classList.add("is-pressed");
			setTimeout(function () {
				control.classList.remove("is-pressed");
			}, 400);
		});
	}

	// ---------------------------------------------------------------- language
	//
	// The preference is saved and, at the same time, the page is fetched in
	// the new language (`?_lang=`, which Frappe honours for one request);
	// the app is then swapped in place, the direction and the other
	// direction's stylesheets with it (see swap). One round trip instead of
	// a save, a reload and everything parsed again. Pages remembered in the
	// old language are forgotten. Anything unexpected reloads, as before.

	function setupLanguage(control) {
		control.addEventListener("click", function () {
			var language = control.getAttribute("data-kui-language");
			if (!language || control.classList.contains("is-active")) return;
			control.disabled = true;
			var here = new URL(window.location.href);
			var target = new URL(here.href);
			target.searchParams.set("_lang", language);
			var saved = fetch("/api/method/kuwait_localization.api.language.set_user_language", {
				method: "POST",
				credentials: "same-origin",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-Frappe-CSRF-Token": csrf(),
				},
				body: JSON.stringify({ language: language }),
			});
			Promise.all([saved, fetchPage(target)])
				.then(function (results) {
					if (!results[0].ok) throw new Error(String(results[0].status));
					var fresh = { url: here, html: results[1].html };
					if (!swap(fresh)) throw new Error("not swappable");
					forgetPages();
					remember(fresh);
				})
				.catch(function () {
					window.location.reload();
				});
		});
	}

	// The stylesheets of the other direction (data-kui-warm, on the page
	// with the language switch) are fetched once the page is idle, so the
	// switch itself has nothing left to download.
	function warmStyles(element) {
		var hrefs = (element.getAttribute("data-kui-warm") || "").split(/\s+/).filter(Boolean);
		if (!hrefs.length || !window.fetch) return;
		setTimeout(function () {
			hrefs.forEach(function (href) {
				fetch(href, { credentials: "same-origin" }).catch(function () {});
			});
		}, 1200);
	}

	// ----------------------------------------------------------------- logout
	//
	// Frappe accepts logout only as a POST (a plain link answers 403 "Not
	// Permitted"), so the control posts with the CSRF token and then goes to
	// the login page. The control's own href is the no-script fallback.

	function setupLogout(control) {
		control.addEventListener("click", function (event) {
			event.preventDefault();
			if (control.getAttribute("aria-disabled") === "true") return;
			control.setAttribute("aria-disabled", "true");
			fetch("/api/method/logout", {
				method: "POST",
				credentials: "same-origin",
				headers: { Accept: "application/json", "X-Frappe-CSRF-Token": csrf() },
			})
				.then(function (response) {
					if (!response.ok) throw new Error(String(response.status));
					forgetPages();
					window.location.href = "/login";
				})
				.catch(function () {
					control.removeAttribute("aria-disabled");
				});
		});
	}

	// ------------------------------------------------------------------- theme
	//
	// Light, dark, or the phone's own setting ("auto"). Light unless the
	// person chose otherwise (decided 2026-11-04: the default is light, not
	// the phone's setting). The choice stays on the phone (it is about this
	// screen, not the account) and is stamped on <html> as data-kui-theme;
	// kui_page.html stamps it again before the first paint.

	var THEME_KEY = "kui-theme";

	function storedTheme() {
		try {
			var value = window.localStorage.getItem(THEME_KEY);
			return value === "dark" || value === "auto" ? value : "light";
		} catch (e) {
			return "light";
		}
	}

	function applyTheme(value) {
		var rootEl = document.documentElement;
		if (value === "light" || value === "dark") rootEl.setAttribute("data-kui-theme", value);
		else rootEl.removeAttribute("data-kui-theme");
	}

	function setupTheme(group) {
		var buttons = group.querySelectorAll("[data-kui-theme-set]");
		function mark(value) {
			buttons.forEach(function (button) {
				var on = button.getAttribute("data-kui-theme-set") === value;
				button.classList.toggle("is-active", on);
				button.setAttribute("aria-pressed", on ? "true" : "false");
			});
		}
		mark(storedTheme());
		buttons.forEach(function (button) {
			button.addEventListener("click", function () {
				var value = button.getAttribute("data-kui-theme-set");
				try {
					window.localStorage.setItem(THEME_KEY, value);
				} catch (e) {
					// private window: the choice lasts for this page only
				}
				applyTheme(value);
				mark(value);
			});
		});
	}

	// --------------------------------------------------------------- text size
	//
	// Normal, large or larger, kept on the phone like the theme and stamped on
	// <html> as data-kui-text (the type scale in _kui_tokens.scss follows it).

	var TEXT_KEY = "kui-text";
	var TEXT_SIZES = ["large", "larger"];

	function setupTextSize(group) {
		var buttons = group.querySelectorAll("[data-kui-text-set]");
		function stored() {
			try {
				var value = window.localStorage.getItem(TEXT_KEY);
				return TEXT_SIZES.indexOf(value) === -1 ? "normal" : value;
			} catch (e) {
				return "normal";
			}
		}
		function mark(value) {
			buttons.forEach(function (button) {
				var on = button.getAttribute("data-kui-text-set") === value;
				button.classList.toggle("is-active", on);
				button.setAttribute("aria-pressed", on ? "true" : "false");
			});
		}
		mark(stored());
		buttons.forEach(function (button) {
			button.addEventListener("click", function () {
				var value = button.getAttribute("data-kui-text-set");
				var chosen = TEXT_SIZES.indexOf(value) !== -1;
				try {
					if (chosen) window.localStorage.setItem(TEXT_KEY, value);
					else window.localStorage.removeItem(TEXT_KEY);
				} catch (e) {
					// private window: the choice lasts for this page only
				}
				if (chosen) document.documentElement.setAttribute("data-kui-text", value);
				else document.documentElement.removeAttribute("data-kui-text");
				mark(chosen ? value : "normal");
			});
		});
	}

	// ------------------------------------------------------------ notifications
	//
	// Frappe tells people things with a dialog in the middle of the screen.
	// On the product's pages a message is a small card in the top corner
	// instead (the end corner, so the right in English and the left in
	// Arabic): it does not block the page, goes away by itself, and a tap
	// closes it. A message that asks something (it carries buttons) keeps
	// Frappe's dialog; so does every page that is not the product's.
	// Messages are shown as text: markup from the server is not rendered.

	var TOAST_TONES = { red: "danger", orange: "warning", yellow: "warning", green: "ok", blue: "info" };

	function toastHost() {
		if (!document.querySelector(".kui-app")) return null;
		// On <body>, not inside .kui-app: a tap that moves to the next page
		// swaps .kui-app, and "Saved" has to outlive that.
		var host = document.querySelector("body > .kui-toasts");
		if (!host) {
			host = document.createElement("div");
			host.className = "kui-toasts";
			host.setAttribute("aria-live", "polite");
			document.body.appendChild(host);
		}
		return host;
	}

	function plainText(html) {
		var box = document.createElement("div");
		box.innerHTML = String(html == null ? "" : html).replace(/<br\s*\/?>|<\/(p|li|div)>/gi, "\n");
		return (box.textContent || "").replace(/\n{2,}/g, "\n").trim();
	}

	function toast(message, tone, title) {
		var host = toastHost();
		var text = plainText(message);
		if (!host || !text) return false;
		tone = tone || "info";
		var card = document.createElement("div");
		card.className = "kui-toast is-" + tone;
		card.setAttribute("role", tone === "danger" ? "alert" : "status");
		card.setAttribute("dir", "auto");
		if (title) {
			var head = document.createElement("strong");
			head.textContent = plainText(title);
			card.appendChild(head);
		}
		var body = document.createElement("span");
		body.textContent = text;
		card.appendChild(body);
		var timer = null;
		function close() {
			if (timer) window.clearTimeout(timer);
			card.classList.add("is-leaving");
			window.setTimeout(function () {
				if (card.parentNode) card.parentNode.removeChild(card);
			}, 180);
		}
		card.addEventListener("click", close);
		// Long enough to read: about a second per dozen characters, and an
		// error stays longer because it usually needs acting on.
		var life = Math.min(15000, (tone === "danger" ? 7000 : 4000) + text.length * 45);
		timer = window.setTimeout(close, life);
		while (host.children.length >= 3) host.removeChild(host.firstChild);
		host.appendChild(card);
		return true;
	}

	function asMessage(msg, title) {
		if (msg && typeof msg === "object" && !Array.isArray(msg)) return msg;
		if (typeof msg === "string" && msg.charAt(0) === "{") {
			try {
				return JSON.parse(msg);
			} catch (e) {
				// not JSON after all: an ordinary sentence that starts with a brace
			}
		}
		return { message: msg, title: title };
	}

	function setupToasts() {
		var f = window.frappe;
		if (!f || f.__kuiToasts || typeof f.msgprint !== "function") return;
		f.__kuiToasts = true;
		var dialog = f.msgprint;
		f.msgprint = function (msg, title) {
			if (!msg || !document.querySelector(".kui-app")) return dialog.apply(this, arguments);
			if (Array.isArray(msg)) {
				msg.forEach(function (one) {
					f.msgprint(one);
				});
				return;
			}
			var data = asMessage(msg, title);
			// Anything that needs an answer or a table stays a dialog.
			if (data.primary_action || data.secondary_action || data.as_table || data.as_list || data.wide || Array.isArray(data.message)) {
				return dialog.apply(this, arguments);
			}
			var tone = TOAST_TONES[data.indicator] || (data.raise_exception ? "danger" : "info");
			if (!toast(data.message, tone, data.title)) return dialog.apply(this, arguments);
		};
		var alertBox = f.show_alert;
		f.show_alert = f.toast = function (message) {
			if (!document.querySelector(".kui-app")) return alertBox.apply(this, arguments);
			var data = typeof message === "object" && message ? message : { message: message };
			if (!toast(data.message || data.body, TOAST_TONES[data.indicator] || "info", data.title)) {
				return alertBox.apply(this, arguments);
			}
		};
	}

	// ------------------------------------------------------ notifications centre
	//
	// The bell in the header opens the panel of what is waiting on the person
	// (scans to confirm, the bank statement, the accountant's questions). The
	// page draws the panel once, closed; both headers' bells open the same
	// one. Escape, the backdrop and the cross close it; following a row
	// swaps the page, which brings a fresh, closed panel with it.

	function notifyPanel() {
		return document.getElementById("kui-notify");
	}

	function setNotify(open) {
		var panel = notifyPanel();
		if (!panel) return;
		panel.hidden = !open;
		document.querySelectorAll("[data-kui-bell]").forEach(function (bellButton) {
			bellButton.setAttribute("aria-expanded", open ? "true" : "false");
		});
		if (open) {
			var close = panel.querySelector(".kui-notify-x");
			if (close) close.focus();
		}
	}

	function setupBell(bellButton) {
		bellButton.addEventListener("click", function () {
			var panel = notifyPanel();
			setNotify(panel ? panel.hidden : false);
		});
	}

	function setupNotifyClose(control) {
		control.addEventListener("click", function () {
			setNotify(false);
		});
	}

	// One listener, however many pages are swapped in.
	document.addEventListener("keydown", function (event) {
		var panel = notifyPanel();
		if (event.key === "Escape" && panel && !panel.hidden) setNotify(false);
	});

	// ----------------------------------------------------------- sign-in lockup
	//
	// Frappe's sign-in card shows the logo and "Sign In". Where the logo is
	// our own mark, the product's name and tagline go under it: the identity's
	// lockup, without overriding Frappe's login template. A site whose owner
	// uploaded a logo has another src and gets nothing added. The words
	// mirror kuwait_localization/branding.py (a test keeps the two equal).

	var BRAND = {
		mark: "/assets/kuwait_localization/images/brand/mark.svg",
		name: { en: "EZ Bill", ar: "إيزي بِل" },
		tagline: { en: "Your books, done.", ar: "دفاترك جاهزة." },
	};

	function setupSignInLockup(logo) {
		if ((logo.getAttribute("src") || "").split("?")[0] !== BRAND.mark) return;
		var head = logo.parentNode;
		if (!head || head.querySelector(".kui-signin-brand")) return;
		var lang = (document.documentElement.getAttribute("lang") || "en").slice(0, 2) === "ar" ? "ar" : "en";
		var brand = document.createElement("div");
		brand.className = "kui-signin-brand";
		[["kui-signin-name", BRAND.name[lang]], ["kui-signin-rule", ""], ["kui-signin-tagline", BRAND.tagline[lang]]].forEach(
			function (part) {
				var el = document.createElement("span");
				el.className = part[0];
				if (part[1]) el.textContent = part[1];
				else el.setAttribute("aria-hidden", "true");
				brand.appendChild(el);
			}
		);
		head.insertBefore(brand, logo.nextSibling);
		head.classList.add("has-kui-brand");
	}

	// ------------------------------------------------------------------- setup

	function setup(root) {
		root.querySelectorAll(".page-card-head img.app-logo").forEach(setupSignInLockup);
		root.querySelectorAll("[data-kui-theme-choice]").forEach(setupTheme);
		root.querySelectorAll("[data-kui-text-choice]").forEach(setupTextSize);
		root.querySelectorAll("[data-kui-bell]").forEach(setupBell);
		root.querySelectorAll("[data-kui-notify-close]").forEach(setupNotifyClose);
		root.querySelectorAll("[data-kui-search]").forEach(setupSearch);
		root.querySelectorAll('[data-kui-action="remind"]').forEach(setupRemind);
		root.querySelectorAll("[data-kui-language]").forEach(setupLanguage);
		root.querySelectorAll("[data-kui-warm]").forEach(warmStyles);
		root.querySelectorAll("[data-kui-logout]").forEach(setupLogout);
	}

	// -------------------------------------------------------------- navigation
	//
	// Every page of the product is rendered on the server. Left alone, each
	// tap reloads the whole document: the stylesheet, Frappe's web bundle and
	// the icon sprite are parsed again, the bottom bar blinks, and on a phone
	// far from the server that is most of the wait. Instead a tap fetches the
	// next page, and the `.kui-app` element is swapped for the new one: same
	// server-rendered markup, no reload. The page's own inline scripts run
	// again (they use `frappe.ready`, which is replayed for the new page).
	//
	// A link loads the normal way when it leaves the product: another origin,
	// the desk (/app), the API, a file, a download, a page that brings its own
	// external scripts, or a response that is not a signed-in product page
	// (the session ended, the login page came back). Any failure falls back to
	// the normal load too, so the worst case is what happens today.
	//
	// A touch on a link starts the fetch before the tap completes, which
	// hides most of the network round trip.
	//
	// On a slow connection even that round trip is the whole wait, so the
	// phone keeps the last copy of each page it showed (localStorage, keyed
	// by the signed-in account). A tap on a page seen before shows that copy
	// at once and fetches the fresh one behind it; when the fresh one lands
	// it replaces the copy quietly, unless the person has started filling a
	// form in the meantime — then the copy stays and the fresh one is kept
	// for next time. An ended session still ends the same way: the fresh
	// response is the login page, which is not swappable, so it loads.

	var OUTSIDE = /^\/(app|api|assets|files|private|login|logout|update-password|printview|desk)(\/|$)/;
	var PREFETCH_TTL = 8000;
	var prefetched = {};
	var pending = null;

	// ------------------------------------------------------- recent pages
	var STORE_KEY = "kui.pages";
	var STORE_PAGES = 12;
	var STORE_PAGE_BYTES = 80000;
	var STORE_TTL = 6 * 60 * 60 * 1000;
	var store = null;

	function currentUser() {
		var app = document.querySelector(".kui-app");
		return (app && app.getAttribute("data-kui-user")) || "";
	}

	function loadStore() {
		if (store) return store;
		store = { user: currentUser(), pages: {} };
		try {
			var saved = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
			if (saved && saved.user === store.user && saved.pages) store.pages = saved.pages;
		} catch (e) {}
		return store;
	}

	function saveStore() {
		try {
			window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
		} catch (e) {}
	}

	function forgetPages() {
		store = null;
		try {
			window.localStorage.removeItem(STORE_KEY);
		} catch (e) {}
	}

	function remember(page) {
		if (!page.html || page.html.length > STORE_PAGE_BYTES) return;
		var s = loadStore();
		s.pages[page.url.href] = { html: page.html, at: Date.now() };
		var keys = Object.keys(s.pages).sort(function (a, b) {
			return s.pages[b].at - s.pages[a].at;
		});
		keys.slice(STORE_PAGES).forEach(function (key) {
			delete s.pages[key];
		});
		saveStore();
	}

	function recall(url) {
		var entry = loadStore().pages[url.href];
		if (!entry || Date.now() - entry.at > STORE_TTL) return null;
		return { url: url, html: entry.html, recalled: true };
	}

	// Something typed, chosen or focused inside the app: a fresh copy must
	// not take it away.
	function busy(root) {
		var active = document.activeElement;
		if (active && root.contains(active) && /^(INPUT|SELECT|TEXTAREA)$/.test(active.tagName)) return true;
		return Array.prototype.some.call(root.querySelectorAll("input, textarea, select"), function (field) {
			if (field.type === "checkbox" || field.type === "radio") return field.checked !== field.defaultChecked;
			if (field.tagName === "SELECT") {
				return Array.prototype.some.call(field.options, function (option) {
					return option.selected !== option.defaultSelected;
				});
			}
			if (field.type === "file") return field.files && field.files.length > 0;
			return field.value !== field.defaultValue;
		});
	}

	function pageUrl(anchor, event) {
		if (event) {
			if (event.defaultPrevented || event.button !== 0) return null;
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
		}
		if (!anchor || anchor.hasAttribute("download") || anchor.hasAttribute("data-kui-full")) return null;
		if (anchor.target && anchor.target !== "_self") return null;
		var href = anchor.getAttribute("href");
		if (!href || href.charAt(0) === "#") return null;
		var url;
		try {
			url = new URL(anchor.href, window.location.href);
		} catch (e) {
			return null;
		}
		if (url.origin !== window.location.origin) return null;
		if (url.protocol !== "http:" && url.protocol !== "https:") return null;
		if (OUTSIDE.test(url.pathname)) return null;
		if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) return null;
		return url;
	}

	function fetchPage(url) {
		return fetch(url.href, {
			credentials: "same-origin",
			headers: { Accept: "text/html" },
		}).then(function (response) {
			if (!response.ok) throw new Error(String(response.status));
			if ((response.headers.get("Content-Type") || "").indexOf("text/html") === -1) throw new Error("not a page");
			return response.text().then(function (html) {
				return { url: new URL(response.url || url.href, window.location.href), html: html };
			});
		});
	}

	function prefetch(url) {
		var key = url.href;
		var entry = prefetched[key];
		if (entry && Date.now() - entry.at < PREFETCH_TTL) return entry.promise;
		var promise = fetchPage(url);
		promise.catch(function () {
			delete prefetched[key];
		});
		prefetched[key] = { at: Date.now(), promise: promise };
		return promise;
	}

	function takePrefetched(url) {
		var entry = prefetched[url.href];
		delete prefetched[url.href];
		return entry && Date.now() - entry.at < PREFETCH_TTL ? entry.promise : null;
	}

	// The next page must bring no external script this document lacks. (The
	// document may hold more: Frappe's web bundle loads some on demand.)
	function bringsNewScripts(doc) {
		return Array.prototype.some.call(doc.querySelectorAll("script[src]"), function (script) {
			var src = script.getAttribute("src");
			return !Array.prototype.some.call(document.querySelectorAll("script[src]"), function (mine) {
				return mine.getAttribute("src") === src;
			});
		});
	}

	// Resolves once every given stylesheet has loaded (or failed), or after
	// a moment regardless, so a slow one never leaves the page unstyled.
	function loaded(links) {
		return new Promise(function (resolve) {
			var left = links.length;
			var timer = setTimeout(resolve, 3000);
			if (!left) {
				clearTimeout(timer);
				resolve();
				return;
			}
			links.forEach(function (link) {
				function one() {
					if (--left === 0) {
						clearTimeout(timer);
						resolve();
					}
				}
				link.addEventListener("load", one);
				link.addEventListener("error", one);
			});
		});
	}

	function copyAttribute(name, from, to) {
		var value = from.getAttribute(name);
		if (value === null) to.removeAttribute(name);
		else to.setAttribute(name, value);
	}

	// Scripts inside parsed markup never run when inserted; a fresh element
	// with the same text does.
	function runScripts(root) {
		root.querySelectorAll("script").forEach(function (old) {
			var script = document.createElement("script");
			Array.prototype.forEach.call(old.attributes, function (attribute) {
				script.setAttribute(attribute.name, attribute.value);
			});
			script.textContent = old.textContent;
			old.parentNode.replaceChild(script, old);
		});
	}

	function swap(page) {
		var doc = new DOMParser().parseFromString(page.html, "text/html");
		var next = doc.querySelector(".kui-app");
		var current = document.querySelector(".kui-app");
		if (!next || !current) return false;
		if (doc.body.getAttribute("frappe-session-status") !== "logged-in") return false;
		if (bringsNewScripts(doc)) return false;

		// A stylesheet the next page has and this one lacks (a feature app's).
		// When the direction changes, the stylesheets of the old direction go
		// once the new ones have loaded: the two sets would fight otherwise.
		var added = [];
		var wanted = {};
		doc.querySelectorAll('link[rel="stylesheet"][href]').forEach(function (link) {
			var href = link.getAttribute("href");
			wanted[href] = true;
			if (!document.querySelector('link[rel="stylesheet"][href="' + href + '"]')) {
				var node = document.importNode(link, true);
				document.head.appendChild(node);
				added.push(node);
			}
		});
		var dirChanged = doc.documentElement.getAttribute("dir") !== document.documentElement.getAttribute("dir");
		if (dirChanged) {
			var stale = Array.prototype.filter.call(document.querySelectorAll('link[rel="stylesheet"][href]'), function (link) {
				return !wanted[link.getAttribute("href")];
			});
			loaded(added).then(function () {
				stale.forEach(function (link) {
					link.parentNode && link.parentNode.removeChild(link);
				});
			});
			if (window.frappe && window.frappe.boot) window.frappe.boot.lang = doc.documentElement.getAttribute("lang");
		}

		document.title = doc.title;
		copyAttribute("lang", doc.documentElement, document.documentElement);
		copyAttribute("dir", doc.documentElement, document.documentElement);
		copyAttribute("data-path", doc.body, document.body);
		copyAttribute("class", doc.body, document.body);
		var wrapper = current.closest("[data-path]");
		var nextWrapper = next.closest("[data-path]");
		if (wrapper && nextWrapper && wrapper !== document.body) {
			copyAttribute("id", nextWrapper, wrapper);
			copyAttribute("data-path", nextWrapper, wrapper);
		}

		var events = window.frappe && Array.isArray(window.frappe.ready_events) ? window.frappe.ready_events : null;
		var before = events ? events.length : 0;
		var fresh = document.importNode(next, true);
		current.parentNode.replaceChild(fresh, current);
		runScripts(fresh);
		if (events) {
			events.slice(before).forEach(function (fn) {
				try {
					fn();
				} catch (e) {
					if (window.console) console.error("kui: page script failed", e);
				}
			});
		}
		setup(fresh);
		return true;
	}

	function arrive(page, options) {
		if (options.push !== false) {
			history.replaceState({ kui: true, scroll: window.scrollY }, "", window.location.href);
			history.pushState({ kui: true, scroll: 0 }, "", page.url.href);
		}
		if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
		window.scrollTo(0, options.scroll || 0);
	}

	function navigate(url, options) {
		options = options || {};
		var token = {};
		pending = token;
		var promise = takePrefetched(url) || fetchPage(url);
		var shown = recall(url);
		if (shown && swap(shown)) arrive(shown, options);
		else shown = null;
		var root = document.querySelector(".kui-app");
		if (root) root.classList.add("is-loading");
		return promise
			.then(function (page) {
				if (pending !== token) return;
				pending = null;
				var app = document.querySelector(".kui-app");
				if (shown) {
					// The copy is on screen; the fresh page replaces it only when
					// it differs and nothing is being filled in. Either way it is
					// what the next tap shows.
					if (page.html !== shown.html && app && !busy(app)) {
						var scroll = window.scrollY;
						if (!swap(page)) {
							window.location.href = page.url.href;
							return;
						}
						window.scrollTo(0, scroll);
					} else if (app) {
						app.classList.remove("is-loading");
					}
					if (page.url.href === shown.url.href) remember(page);
					return;
				}
				if (!swap(page)) {
					window.location.href = page.url.href;
					return;
				}
				remember(page);
				arrive(page, options);
			})
			.catch(function () {
				if (pending !== token) return;
				pending = null;
				if (shown) {
					if (root) root.classList.remove("is-loading");
					return;
				}
				window.location.href = url.href;
			});
	}

	function setupNavigation() {
		if (!window.fetch || !window.DOMParser || !window.history || !window.history.pushState) return;
		if (!document.querySelector(".kui-app")) return;
		if ("scrollRestoration" in history) history.scrollRestoration = "manual";

		document.addEventListener("click", function (event) {
			var anchor = event.target.closest ? event.target.closest("a[href]") : null;
			if (!anchor || !document.querySelector(".kui-app")) return;
			if (!document.querySelector(".kui-app").contains(anchor)) return;
			var url = pageUrl(anchor, event);
			if (!url) return;
			event.preventDefault();
			navigate(url);
		});

		function warm(event) {
			var anchor = event.target.closest ? event.target.closest("a[href]") : null;
			var url = anchor && pageUrl(anchor, null);
			if (url && document.querySelector(".kui-app") && document.querySelector(".kui-app").contains(anchor)) prefetch(url);
		}
		document.addEventListener("touchstart", warm, { passive: true });
		document.addEventListener("mousedown", warm, { passive: true });

		window.addEventListener("popstate", function (event) {
			if (!event.state || !event.state.kui) {
				window.location.reload();
				return;
			}
			navigate(new URL(window.location.href), { push: false, scroll: event.state.scroll || 0 });
		});
		history.replaceState({ kui: true, scroll: window.scrollY }, "", window.location.href);
	}

	// For feature pages: `kui.navigate("/expenses/E-0001")` after a save
	// keeps the product on screen instead of reloading it.
	window.kui = window.kui || {};
	window.kui.navigate = function (href) {
		var url;
		try {
			url = new URL(href, window.location.href);
		} catch (e) {
			window.location.href = href;
			return;
		}
		if (url.origin !== window.location.origin || OUTSIDE.test(url.pathname) || !document.querySelector(".kui-app")) {
			window.location.href = url.href;
			return;
		}
		navigate(url);
	};
	// `kui.refresh()` after a change made on the server (a confirmed figure, a
	// chosen currency): the same page again, swapped in where it stands, so
	// the person stays where they were reading instead of being sent to the
	// top by a reload. Falls back to the reload when a swap is not possible.
	window.kui.refresh = function () {
		var root = document.querySelector(".kui-app");
		var url;
		try {
			url = new URL(window.location.href);
		} catch (e) {
			root = null;
		}
		if (!root) {
			window.location.reload();
			return Promise.resolve();
		}
		var scroll = window.scrollY;
		root.classList.add("is-loading");
		return fetchPage(url)
			.then(function (page) {
				if (!swap(page)) {
					window.location.reload();
					return;
				}
				window.scrollTo(0, scroll);
				remember(page);
			})
			.catch(function () {
				window.location.reload();
			});
	};
	window.kui.prefetch = function (href) {
		try {
			prefetch(new URL(href, window.location.href));
		} catch (e) {}
	};

	window.kui.toast = toast;
	// For a change that redraws the chrome of every page (the bottom bar's
	// tabs): pages remembered on the phone still show the old one.
	window.kui.forgetPages = forgetPages;

	ready(function () {
		setupToasts();
		setup(document);
		setupNavigation();
	});
})();

// ------------------------------------------------------------ browser errors
//
// During the beta a script error on someone's phone is sent to the server
// (kuwait_localization.watch.client_error) and shows on the platform's Beta
// watch next to the server's own errors. Signed-in people only, at most ten
// per page load, the same message once.
(function () {
	var sent = 0;
	var seen = {};
	function report(message, source, line) {
		try {
			if (sent >= 10 || !window.fetch) return;
			if (!(window.frappe && frappe.is_user_logged_in && frappe.is_user_logged_in())) return;
			var key = String(message).slice(0, 200);
			if (seen[key]) return;
			seen[key] = 1;
			sent += 1;
			var body = new URLSearchParams({
				message: key,
				source: String(source || "").slice(0, 300),
				line: String(line || 0),
				path: window.location.pathname,
			});
			fetch("/api/method/kuwait_localization.watch.client_error", {
				method: "POST",
				credentials: "same-origin",
				headers: { "X-Frappe-CSRF-Token": (window.frappe && frappe.csrf_token) || "", Accept: "application/json" },
				body: body,
				keepalive: true,
			}).catch(function () {});
		} catch (e) {}
	}
	window.addEventListener("error", function (event) {
		report(event.message || "error", event.filename, event.lineno);
	});
	window.addEventListener("unhandledrejection", function (event) {
		var reason = event.reason;
		report("Unhandled: " + ((reason && (reason.message || reason.statusText)) || String(reason)), "", 0);
	});
})();
